import { mdiRefresh } from "@mdi/js";
import Icon from "@mdi/react";
import { FunctionComponent, useEffect, useState } from "react";

import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { ApiResponse } from "../../../../service/models/response";
import { GetUserSessionLogs } from "../../../../service/repos/session_logs_repo";
import LoadingWidget from "../../../components/loading_spinner";
import Pagination from "../../../new_components/common/pagination";

interface SessionLog {
  id?: string;
  user_id?: string;
  user_name?: string;
  email?: string;
  is_emp?: boolean;
  event?: "login" | "logout";
  auth_method?: string;
  ip?: string;
  user_agent?: string;
  success?: boolean;
  failure_reason?: string;
  created_at?: string;
}

const heads = [
  "Date & Time",
  "Name",
  "User ID",
  "Email",
  "Type",
  "Event",
  "Method",
  "Status",
  "Failure Reason",
];

const SessionLogsScreen: FunctionComponent = () => {
  const { showToast } = useToast();

  const [logs, setLogs] = useState<SessionLog[]>([]);
  const [page, setPage] = useState(1);
  const [count] = useState(50);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [userName, setUserName] = useState("");
  const [userId, setUserId] = useState("");
  const [email, setEmail] = useState("");
  const [event, setEvent] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const getLogs = async () => {
    setLoading(true);
    const res: ApiResponse = await GetUserSessionLogs({
      page,
      count,
      user_name: userName || undefined,
      user_id: userId || undefined,
      email: email || undefined,
      event: event || undefined,
      from: from ? new Date(from).toISOString() : undefined,
      to: to ? new Date(`${to}T23:59:59.999`).toISOString() : undefined,
    });

    if (res.success) {
      setLogs(res.data?.logs ?? []);
      const meta = res.data?.metadata?.[0];
      setTotal(meta?.total ?? 0);
    } else {
      showToast({ type: ToastType.error, text: res.error ?? "" });
    }
    setLoading(false);
  };

  useEffect(() => {
    getLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const applyFilters = () => {
    if (page !== 1) setPage(1);
    else getLogs();
  };

  const clearFilters = () => {
    setUserName("");
    setUserId("");
    setEmail("");
    setEvent("");
    setFrom("");
    setTo("");
    if (page !== 1) setPage(1);
    else getLogs();
  };

  const inputClass =
    "text-xs md:text-sm px-3 h-8 rounded border focus:outline-none bg-white w-full";

  return (
    <div className="h-screen w-full overflow-hidden px-3 md:px-5 text-xs md:text-sm">
      <div className="flex justify-between items-center flex-wrap my-3">
        <h1 className="font-bold text-xl md:text-2xl text-gray-500 flex items-center gap-3">
          User Session Logs
          <button id="session_logs_refresh_btn" onClick={() => getLogs()} title="Refresh">
            <Icon path={mdiRefresh} className="h-6 w-6" />
          </button>
        </h1>
      </div>

      <form
        id="session_logs_filter_form"
        className="grid grid-cols-2 gap-2 md:flex md:flex-wrap md:items-end mb-3"
        onSubmit={(e) => {
          e.preventDefault();
          applyFilters();
        }}
      >
        <div className="flex flex-col">
          <label className="text-xs text-gray-500 mb-1">Name</label>
          <input
            id="session_logs_name_filter"
            className={inputClass}
            value={userName}
            placeholder="User name"
            onChange={(e) => setUserName(e.target.value)}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-xs text-gray-500 mb-1">User ID</label>
          <input
            id="session_logs_user_id_filter"
            className={inputClass}
            value={userId}
            placeholder="User ID"
            onChange={(e) => setUserId(e.target.value)}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-xs text-gray-500 mb-1">Email</label>
          <input
            id="session_logs_email_filter"
            className={inputClass}
            value={email}
            placeholder="Email"
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-xs text-gray-500 mb-1">Event</label>
          <select
            id="session_logs_event_filter"
            className={inputClass}
            value={event}
            onChange={(e) => setEvent(e.target.value)}
          >
            <option value="">All</option>
            <option value="login">Login</option>
            <option value="logout">Logout</option>
          </select>
        </div>
        <div className="flex flex-col">
          <label className="text-xs text-gray-500 mb-1">From</label>
          <input
            id="session_logs_from_filter"
            type="date"
            className={inputClass}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-xs text-gray-500 mb-1">To</label>
          <input
            id="session_logs_to_filter"
            type="date"
            className={inputClass}
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
        <div className="col-span-2 flex gap-2 md:contents">
          <button
            id="session_logs_apply_btn"
            type="submit"
            className="flex-1 md:flex-none rounded-lg bg-myPrimaryColor text-white px-4 h-8 text-xs md:text-sm"
          >
            Apply
          </button>
          <button
            id="session_logs_clear_btn"
            type="button"
            onClick={clearFilters}
            className="flex-1 md:flex-none rounded-lg border px-4 h-8 text-xs md:text-sm hover:bg-gray-100"
          >
            Clear
          </button>
        </div>
      </form>

      <div id="session_logs_table" className="overflow-auto" style={{ height: "75%" }}>
        <table className="border divide-y divide-gray-200 relative w-full hidden md:table">
          <thead className="bg-gray-200 sticky top-0 z-10">
            <tr>
              {heads.map((h) => (
                <th
                  key={h}
                  scope="col"
                  className="px-3 py-2 text-xs font-medium text-center text-black uppercase tracking-wider"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 text-xs">
            {logs.map((log) => (
              <tr id={`session_logs_row_${log.id}`} className="hover:bg-gray-200" key={log.id}>
                <td className="px-3 py-2 whitespace-nowrap text-center">
                  {log.created_at
                    ? new Date(log.created_at).toLocaleString()
                    : "--"}
                </td>
                <td className="px-3 py-2 whitespace-nowrap font-medium text-gray-900 text-center">
                  {log.user_name && log.user_name.trim() !== ""
                    ? log.user_name
                    : "--"}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-center">
                  {log.user_id ?? "--"}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-center">
                  {log.email ?? "--"}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-center">
                  {log.is_emp ? "Employee" : "Customer"}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-center">
                  <span
                    className={`px-2 py-1 rounded-full text-white ${
                      log.event === "login" ? "bg-blue-400" : "bg-gray-400"
                    }`}
                  >
                    {log.event ?? "--"}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-center">
                  {log.auth_method ?? "--"}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-center">
                  <span
                    className={log.success ? "text-green-600" : "text-red-600"}
                  >
                    {log.success ? "Success" : "Failed"}
                  </span>
                </td>
                <td className="px-3 py-2 text-center">
                  <div
                    title={log.failure_reason}
                    className="max-w-xs overflow-hidden overflow-ellipsis whitespace-nowrap"
                  >
                    {log.failure_reason ?? "--"}
                  </div>
                </td>
              </tr>
            ))}
            {logs.length === 0 && !loading && (
              <tr>
                <td
                  colSpan={heads.length}
                  className="text-center py-6 text-gray-400"
                >
                  No session logs found
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="md:hidden flex flex-col gap-2 text-xs">
          {logs.map((log) => (
            <div
              id={`session_logs_row_mobile_${log.id}`}
              key={log.id}
              className="border rounded-lg bg-white p-3 shadow-sm"
            >
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium text-gray-900">
                  {log.user_name && log.user_name.trim() !== ""
                    ? log.user_name
                    : "--"}
                </span>
                <span
                  className={`px-2 py-0.5 rounded-full text-white ${
                    log.event === "login" ? "bg-blue-400" : "bg-gray-400"
                  }`}
                >
                  {log.event ?? "--"}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-gray-600">
                <div>
                  <span className="text-gray-400">Date: </span>
                  {log.created_at
                    ? new Date(log.created_at).toLocaleString()
                    : "--"}
                </div>
                <div>
                  <span className="text-gray-400">Status: </span>
                  <span
                    className={log.success ? "text-green-600" : "text-red-600"}
                  >
                    {log.success ? "Success" : "Failed"}
                  </span>
                </div>
                <div className="break-all">
                  <span className="text-gray-400">User ID: </span>
                  {log.user_id ?? "--"}
                </div>
                <div>
                  <span className="text-gray-400">Type: </span>
                  {log.is_emp ? "Employee" : "Customer"}
                </div>
                <div className="break-all col-span-2">
                  <span className="text-gray-400">Email: </span>
                  {log.email ?? "--"}
                </div>
                <div>
                  <span className="text-gray-400">Method: </span>
                  {log.auth_method ?? "--"}
                </div>
                {log.failure_reason && (
                  <div className="col-span-2 text-red-600">
                    <span className="text-gray-400">Failure Reason: </span>
                    {log.failure_reason}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {logs.length === 0 && !loading && (
          <div className="md:hidden text-center py-6 text-gray-400">
            No session logs found
          </div>
        )}
      </div>

      <div id="session_logs_pagination" className="mt-3">
        <Pagination
          itemCount={logs.length}
          page={page}
          total={total}
          count={count}
          onChange={(p) => setPage(p)}
        />
      </div>
      <LoadingWidget loading={loading} />
    </div>
  );
};

export default SessionLogsScreen;
